import { NGXLogger } from 'ngx-logger';
import { Injectable, inject } from '@angular/core';
import { CanActivate, CanLoad, Router, ActivatedRouteSnapshot, RouterStateSnapshot, Route, UrlSegment } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/tally-sheet/auth.service';
import { ROLES } from '../roles';
import { UserService } from '../services/user.service';
import { NetworkStatusService } from '../services/network-status.service';
import { OfflineLeaseService } from '../services/offline-lease.service';
import { PermissionService, GuardSessionSyncResult } from '../services/permission.service';
import { LogoutService } from '../services/logout.service';
import { ForcedLogoutDialogService } from '../services/forced-logout-dialog.service';
import { AppSplashService } from '../services/app-splash.service';
import { offlineDb, summarizeUnsynced, takeRemovedChangesNotice, wipeOfflineDataIfDifferentUser } from '../offline/offline-db';
import { AppConstants } from '../commons/app-constants';
import { ToastMessagesService } from '../commons/services/toast-messages.service';
import { TranslationService } from '../i18n/translation.service';

@Injectable({
  providedIn: 'root'
})
export class AuthSessionGuard implements CanActivate, CanLoad {
  private readonly logger = inject(NGXLogger);
  private readonly toast = inject(ToastMessagesService);
  private readonly translation = inject(TranslationService);


  constructor(
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
    private networkStatusService: NetworkStatusService,
    private offlineLeaseService: OfflineLeaseService,
    private permissionService: PermissionService,
    private logoutService: LogoutService,
    private appSplashService: AppSplashService,
    private forcedLogoutDialogService: ForcedLogoutDialogService,
  ) { }

  // Check if the route can be activated
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.checkAccess(route, state.url);
  }

  // Check if the module can be lazy-loaded
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | boolean {
    return this.checkAccess();
  }

  // General access check based on the user's current step. Runs the session-freshness gate
  // (different-device-user wipe, max-offline-days staleness, first-sync requirement) BEFORE the
  // existing onboarding/subscription checks below — see enforceSessionFreshness().
  private checkAccess(route?: ActivatedRouteSnapshot, requestedUrl?: string): Observable<boolean> {
    return this.permissionService.syncSessionForGuard().pipe(
      switchMap((result) => this.enforceSessionFreshness(result)),
      switchMap((proceed) => proceed ? this.checkOnboardingAndSubscription(requestedUrl) : of(false)),
    );
  }

  // Session-hygiene gate — runs on every guarded navigation, native and web alike (the outcomes
  // that only apply natively — 'cached'/'never-synced' — simply can't occur on web, since
  // syncSessionForGuard() only falls back to a device cache when isOfflineCapableNative()).
  //
  // Consolidates what used to be split across a dead guard (AppGuardGuard.
  // wipeOfflineDataIfDifferentUser — found 2026-08-03 to never actually be wired into any live
  // route; the fix landed in a class Angular never calls) and nothing at all (the 15-day max-
  // offline-days cap and the first-sync requirement are both new).
  //
  // The wipe check itself now lives in offline-db.ts's wipeOfflineDataIfDifferentUser (shared
  // with OfflinePrefetchService — found 2026-08-04 that its own independent online-transition
  // effect() could race ahead of THIS guard's wipe, since neither waited on the other).
  private enforceSessionFreshness(result: GuardSessionSyncResult): Observable<boolean> {
    if (result.outcome === 'never-synced') {
      // Never completed a single successful live sync on this device (and can't reach the
      // server now to do one) — refuse to let the app proceed at all rather than guess at
      // either extreme (blanket-allow or blanket-deny against data that was never real).
      this.logger.warn('[AuthSessionGuard] Refusing navigation — device has never completed an initial sync');
      this.forceSessionReset('never-synced');
      return of(false);
    }

    if (result.syncedAt !== null) {
      const maxMs = result.maxOfflineDays * 24 * 60 * 60 * 1000;
      const ageMs = Date.now() - result.syncedAt;
      if (ageMs > maxMs) {
        this.logger.warn(`[AuthSessionGuard] Session stale — ${Math.floor(ageMs / 86400000)}d since last sync, cap is ${result.maxOfflineDays}d`);
        return from(this.lockOrReset()).pipe(map(() => false));
      }
    }

    if (result.outcome === 'live' && result.email) {
      const email = result.email;
      const tenantId = result.tenantId;
      this.logger.info('[AuthSessionGuard] enforceSessionFreshness live outcome, running wipe check', JSON.stringify({ tenantId }));
      // Splash is on screen at this exact point after a full page reload (login on non-iOS, iOS
      // cross-account login post-fix, company switch — see login.component.ts's navigateAfterLogin
      // and header/side-bar's switchCompany) — a no-op otherwise (e.g. iOS same-user re-login,
      // which deliberately uses in-app navigation with no reload), which is fine: the wipe itself
      // is what matters on every platform/path, the announcement is a bonus only when a splash
      // happens to be showing.
      return from(wipeOfflineDataIfDifferentUser(email, tenantId, () => this.appSplashService.announceAccountSwitch()))
        .pipe(
          switchMap(() => from(this.afterIdentityCheck(email))),
          catchError((err) => {
            this.logger.warn('[AuthSessionGuard] Failed to wipe offline data on user switch', err);
            return of(true);
          }),
        );
    }
    this.logger.info('[AuthSessionGuard] enforceSessionFreshness non-live outcome, wipe check skipped', JSON.stringify({ outcome: result.outcome }));
    return of(true);
  }

  // OFFLINE_PLAN.md 1.7 / D4 (2026-09-29). Past the offline-days limit (this only happens while offline —
  // online, the session is re-synced and the clock restarts): with changes still waiting, lock the app
  // instead of logging out — the old forced logout deleted them. The note lets the lock come back after
  // the user logs in again. Nothing waiting → the forced logout exactly as before.
  private async lockOrReset(): Promise<void> {
    const { count } = await summarizeUnsynced().catch(() => ({ count: 0, kinds: [] as string[] }));
    if (count > 0) {
      const email = localStorage.getItem(AppConstants.LAST_DEVICE_USER_EMAIL);
      if (email) await offlineDb.notices.put({ key: `offlineLock:${email}`, value: String(Date.now()) }).catch(() => undefined);
      this.router.navigate(['/offline-locked']);
      return;
    }
    this.forceSessionReset('stale-session');
  }

  // Runs once the different-user check has finished for a live session (OFFLINE_PLAN.md, 2026-09-29):
  //  - a pending 15-day lock decision for this user (they logged in again to sync) → back to the lock
  //    screen to answer "Sync your old changes?" (D4);
  //  - a "your offline changes were removed" note for this user (D5) → shown once.
  // Native only in practice — both notes are only ever written in the phone app.
  private async afterIdentityCheck(email: string): Promise<boolean> {
    try {
      const lockNote = await offlineDb.notices.get(`offlineLock:${email}`);
      if (lockNote) {
        if ((await summarizeUnsynced()).count > 0) {
          this.router.navigate(['/offline-locked']);
          return false;
        }
        await offlineDb.notices.delete(`offlineLock:${email}`);
      }
      const removed = await takeRemovedChangesNotice(email);
      if (removed) {
        this.toast.showWarningMessage(
          this.translation.instant('offline.removedTitle'),
          this.translation.instant('offline.removedBody').replace('{n}', String(removed.count)),
          true,
        );
      }
    } catch (err) {
      this.logger.warn('[AuthSessionGuard] offline notes check failed', err);
    }
    return true;
  }

  // Fire-and-forget from the guard's point of view — blocking THIS navigation (returning false)
  // is what matters; forceLogout()'s own navigate(['/login']) follows shortly after regardless.
  private forceSessionReset(reason: string): void {
    this.logoutService.forceLogout(reason, { sessionExpired: 'true' }).subscribe({
      error: (err) => this.logger.error('[AuthSessionGuard] forceSessionReset failed', err),
    });
  }

  private checkOnboardingAndSubscription(requestedUrl?: string): Observable<boolean> {
    return this.authService.getProgress().pipe(
      switchMap((userProgress:any) => {
        // Check the user's current role or step (e.g., 'COMPANY_REGISTERED', 'SUBSCRIPTION_VALID')
        const roles = userProgress.roles;
        const registrationStatus = userProgress.registrationStatus;
        const companyRegistered = userProgress.companyRegistered;
        const subscriptionValid = userProgress.subscriptionValid;
        const invitedUser = userProgress.invitedUser;
        this.logger.debug(`userProgress Response is ${JSON.stringify(userProgress)}`)
        this.logger.debug(`userProgress roles are ${JSON.stringify(roles)}`)
        this.logger.debug(`userProgress companyRegistered is ${JSON.stringify(companyRegistered)}`);
        this.logger.debug(`userProgress registrationStatus is ${JSON.stringify(registrationStatus)}`);
        this.logger.debug(`userProgress subscriptionValid is ${JSON.stringify(subscriptionValid)}`);
        this.logger.debug(`userProgress hasRegistrationRole=> ${this.hasRegistrationRole(roles)}`)
        const accountDetailsUrl = '/account-setup/account-details';
        const companyDetailsUrl = '/account-setup/company-details';
        const isOnAccountSetup = (requestedUrl || '').startsWith('/account-setup');
        const isRenewalRoute = this.isRenewalAllowedRoute(requestedUrl || '');
        const subscriptionUrl = '/account-setup/subscription';

        // An invited user (joining an EXISTING company) must never reach Company Registration,
        // Subscription, or Payment — those are for a brand-new company owner, not a teammate.
        // Placed BEFORE the "invitedUser && registrationStatus" bypass just below on purpose:
        // that bypass unconditionally lets a profile-complete invited user through to ANY route,
        // so a block placed after it would never fire for exactly the users most likely to reach
        // these three screens. Real incident (2026-09-06): an invited custom-rights user ended up
        // walking through Company Registration -> Subscription -> Payment with nothing stopping
        // it — this is the structural fix, independent of how they got pointed at these routes.
        const invitedBlockedPaths = [companyDetailsUrl, subscriptionUrl, '/account-setup/payment'];
        if (invitedUser && invitedBlockedPaths.some(p => (requestedUrl || '').startsWith(p))) {
          if (registrationStatus) {
            return this.permissionService.getFirstAccessibleRoute().pipe(
              map((route) => { this.router.navigate([route]); return false; }),
            );
          }
          this.router.navigate([accountDetailsUrl]);
          return of(false);
        }

        if (invitedUser && registrationStatus) {
          return of(true);
        }

        // Affiliate Partner Program (2026-08-23): a shell-tenant outsider affiliate (no real
        // TMS subscription, by design — see AffiliateOnboardingService.createShellIdentity())
        // never has registrationStatus/companyRegistered/subscriptionValid set, because those
        // three checks below exist for the REAL product's onboarding wizard, not the Affiliate
        // Portal. Without this bypass, every one of their logins would get force-redirected
        // straight toward "complete your company" then "pay for a subscription" — confirmed via
        // direct trace of this exact guard, a real bug caught before it shipped. The AFFILIATE
        // role is granted at application/creation time (not deferred to approval), specifically
        // so this bypass also covers a self-serve applicant still PENDING_REVIEW, not just an
        // admin-created ACTIVE one. A real subscriber who is ALSO an affiliate already passes
        // the three checks below normally (their own real company/subscription), so this branch
        // never fires for them — it only matches someone with NO company registered at all.
        if (this.hasAffiliateRole(roles) && !companyRegistered) {
          return of(true);
        }

        // Internal LumberLinq / Rikexim staff (SUPPORT / SALES / SALES_MANAGER, created via the
        // admin Employee-onboarding flow) never go through the customer setup wizard — they live in
        // the dedicated "LumberLinq Internal" company (RBAC-locked to just the CRM).
        // Their completion flags are set at creation now (+ backfilled by prompt212), so this is
        // mostly a safety net; it also stops a brand-new staff account from flashing the wizard in
        // the moment between login and the backend's post-login redirect landing. Mirrors the
        // AFFILIATE bypass just above. Elevated admins already pass via registrationStatus.
        if (this.hasInternalStaffRole(roles)) {
          return of(true);
        }

        if (!registrationStatus) {
          if (!isOnAccountSetup) {
            this.router.navigate([accountDetailsUrl]);
            return of(false);
          }
          return of(true);
        }

        if (!companyRegistered) {
          if (!isOnAccountSetup) {
            this.router.navigate([companyDetailsUrl]);
            return of(false);
          }
          return of(true);
        }

        return this.userService.getSubscriptionStatusDetails().pipe(
          map((subscriptionStatus: any | null) => {
            this.logger.debug(`subscriptionStatus Response is ${JSON.stringify(subscriptionStatus)}`);

            if (subscriptionStatus?.renewalLocked === true) {
              if (isRenewalRoute) {
                return true;
              }
              this.router.navigate([subscriptionUrl], {
                queryParams: { renewal: 'true', locked: 'true' },
                replaceUrl: true
              });
              return false;
            }

            if (!subscriptionValid && !isOnAccountSetup) {
              this.router.navigate([subscriptionUrl]);
              return false;
            }

            return true;
          }),
          catchError(() => {
            if (!subscriptionValid && !isOnAccountSetup) {
              this.router.navigate([subscriptionUrl]);
              return of(false);
            }
            return of(true);
          })
        );
      }),
      catchError((error) => {
        // This session was force-revoked by a login elsewhere (see ForcedLogoutMarker /
        // JwtRequestFilter) — show the same dedicated dialog the HTTP interceptor and RoleGuard
        // show for the identical code, instead of silently redirecting to /login with no
        // explanation.
        if (error?.error?.code === 'SESSION_INVALIDATED_ELSEWHERE') {
          this.logger.warn('[AuthSessionGuard] getProgress 401 traced to a forced-logout-elsewhere marker', error);
          this.forcedLogoutDialogService.open({
            device: error.error?.device || null,
            location: error.error?.location || null,
            kickedAtEpochMillis: error.error?.kickedAtEpochMillis ?? null,
          });
          return of(false);
        }
        // This call fails not just on a real 401 but also on a plain network error — the
        // guard used to redirect to /login in both cases, which broke every offline
        // navigation (the whole point of the offline-grace lease). Only redirect when we're
        // genuinely online (a real auth failure) or the lease itself is invalid/expired.
        //
        // navigator.onLine (NetworkStatusService.isOnline) is unreliable in Android WebViews —
        // it can report true with no real connectivity — so this triggers on EITHER signal:
        // the tracked network status, or (more reliably) the failed request's own status:0,
        // which Angular's HttpClient sets specifically for network-level failures as opposed
        // to a genuine server rejection (401/403/etc, which always has a real status code).
        const isNetworkFailure = !this.networkStatusService.isOnline || error?.status === 0 || error?.status === undefined;
        // Deliberately logged at .error() — environment.prod.ts sets logLevel:"ERROR", so
        // .debug()/.warn() are silently dropped in the production build this app ships as.
        // JSON.stringify the payload — Android WebView's console-to-Logcat bridge just
        // stringifies object args as "[object Object]" instead of serializing them.
        this.logger.error('[AuthSessionGuard] getProgress failed', JSON.stringify({
          isOnline: this.networkStatusService.isOnline,
          errorStatus: error?.status,
          errorMessage: error?.message,
          isNetworkFailure,
        }));
        if (isNetworkFailure) {
          return from(this.offlineLeaseService.check()).pipe(
            map((result) => {
              this.logger.error('[AuthSessionGuard] offline lease check result', JSON.stringify(result));
              if (result.valid && result.withinGrace) {
                return true;
              }
              this.stashReturnUrlAndRedirectToLogin(requestedUrl);
              return false;
            }),
            catchError((leaseError) => {
              this.logger.error('[AuthSessionGuard] offline lease check threw', String(leaseError));
              this.stashReturnUrlAndRedirectToLogin(requestedUrl);
              return of(false);
            }),
          );
        }
        this.stashReturnUrlAndRedirectToLogin(requestedUrl);
        return of(false);
      })
    );
  }

  // Preserves the deep-linked / originally-requested route through the login round-trip —
  // consumed by LoginComponent's plain-success handler and LoginRedirectGuard (both read the
  // same 'postLoginReturnUrl' key already used by ShareDispatcherComponent for protected/
  // private share links). Auth failure only — do not call this from onboarding-state redirects
  // (unregistered/no-company/subscription-invalid), which must run their own flow, not skip
  // straight back to the originally-requested route.
  private stashReturnUrlAndRedirectToLogin(requestedUrl?: string): void {
    if (requestedUrl) {
      localStorage.setItem('postLoginReturnUrl', requestedUrl);
    }
    this.router.navigate(['/login']);
  }

  // Determine the redirect URL based on the user's current step
  private getRedirectUrl(currentStep: string): string {
    switch (currentStep) {
      case 'REGISTRATION':
        return '/register';
      case 'COMPANY':
        return '/company';
      case 'SUBSCRIPTION':
        return '/subscription';
      default:
        return '/auth/login';
    }
  }


  private hasRegistrationRole(roles: any): boolean {
    return  roles.some((role: any) => role.name === ROLES.REGISTRATION);
  }

  private hasCompanyRole(roles: any): boolean {
    return  roles.some((role: any) => role.name === ROLES.COMPANY);
  }

  private hasAffiliateRole(roles: any): boolean {
    return roles.some((role: any) => role.name === ROLES.AFFILIATE);
  }

  private hasInternalStaffRole(roles: any): boolean {
    return Array.isArray(roles) && roles.some((role: any) =>
      role?.name === ROLES.SUPPORT
      || role?.name === ROLES.SALES
      || role?.name === ROLES.SALES_MANAGER);
  }

  private isRenewalAllowedRoute(url: string): boolean {
    const cleanUrl = url.split('?')[0].split('#')[0];
    return cleanUrl === '/account-setup/subscription'
      || cleanUrl === '/account-setup/payment'
      || cleanUrl === '/account-setup/payment-confirmation'
      || cleanUrl === '/account-setup/payment-error';
  }
}
